/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import {
  OrderSummary as OrderSummaryComponent,
  OrderSummaryLineItem,
} from '@/quote-management/components';
import {
  NegotiableQuoteModel,
  QuoteDisplayAmount,
} from '@/quote-management/data/models';
import { state } from '@/quote-management/lib/state';
import { Icon, Price } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Coupon } from '@adobe-commerce/elsie/icons';
import { Container } from '@adobe-commerce/elsie/lib';
import { events } from '@adobe-commerce/event-bus';
import { HTMLAttributes, useEffect, useState } from 'preact/compat';

export interface OrderSummaryProps extends HTMLAttributes<HTMLDivElement> {
  showTotalSaved?: boolean;
  updateLineItems?: (
    lineItems: Array<OrderSummaryLineItem>
  ) => Array<OrderSummaryLineItem>;
}

export const OrderSummary: Container<
  OrderSummaryProps,
  NegotiableQuoteModel | null
> = ({
  children,
  initialData = null,
  showTotalSaved,
  updateLineItems = (lineItems) => lineItems,
  ...props
}) => {
  const [data, setData] = useState<NegotiableQuoteModel | null>(initialData);
  const isVirtual = data?.isVirtual;

  const { quoteDisplaySettings } = state.config;

  const isZeroTax = Boolean(quoteDisplaySettings?.zeroTax);

  const isTaxIncludedInSubtotal =
    quoteDisplaySettings?.subtotal === QuoteDisplayAmount.TAX_INCLUDED;
  const isTaxIncludedAndExcludedInSubtotal =
    quoteDisplaySettings?.subtotal ===
    QuoteDisplayAmount.TAX_INCLUDED_AND_EXCLUDED;

  const isTaxIncludedInShipping =
    quoteDisplaySettings?.shipping === QuoteDisplayAmount.TAX_INCLUDED;
  const isTaxIncludedAndExcludedInShipping =
    quoteDisplaySettings?.shipping ===
    QuoteDisplayAmount.TAX_INCLUDED_AND_EXCLUDED;

  useEffect(() => {
    const dataEvent = events.on(
      'quote-management/quote-data',
      (payload) => {
        setData(payload.quote as NegotiableQuoteModel);
      },
      { eager: true }
    );

    return () => {
      dataEvent?.off();
    };
  }, []);

  const translations = useText({
    free: 'NegotiableQuote.PriceSummary.total.free',
    orderSummary: 'NegotiableQuote.PriceSummary.orderSummary',
    taxToBeDetermined: 'NegotiableQuote.PriceSummary.taxToBeDetermined',
  });

  if (!Object.keys(data ?? {}).length) {
    return null;
  }

  return (
    <OrderSummaryComponent
      {...props}
      data-testid="quote-order-summary"
      heading={<div>{translations.orderSummary}</div>}
      updateLineItems={updateLineItems}
      subTotal={{
        taxIncluded: isTaxIncludedInSubtotal && !isZeroTax,
        taxExcluded: isTaxIncludedAndExcludedInSubtotal,
        priceExcludingTax: (
          <Price
            data-testid="subtotal"
            amount={data?.prices?.subtotalExcludingTax?.value}
            currency={data?.prices?.subtotalExcludingTax?.currency}
          />
        ),
        price:
          (!isZeroTax && isTaxIncludedInSubtotal) ||
          (!isZeroTax && isTaxIncludedAndExcludedInSubtotal) ? (
            <Price
              data-testid="subtotal"
              amount={data?.prices?.subtotalIncludingTax?.value}
              currency={data?.prices?.subtotalIncludingTax?.currency}
            />
          ) : (
            <Price
              data-testid="subtotal"
              amount={data?.prices?.subtotalExcludingTax?.value}
              currency={data?.prices?.subtotalExcludingTax?.currency}
            />
          ),
      }}
      shipping={{
        taxIncluded: isTaxIncludedInShipping && !isZeroTax,
        taxExcluded: isTaxIncludedAndExcludedInShipping,
        priceExcludingTax: (
          <Price
            data-testid="shipping"
            amount={data?.prices?.shippingExcludingTax?.value}
            currency={data?.prices?.shippingExcludingTax?.currency}
          />
        ),
        price:
          (!isZeroTax && isTaxIncludedInShipping) ||
          (!isZeroTax && isTaxIncludedAndExcludedInShipping) ? (
            <Price
              data-testid="shipping"
              amount={data?.prices?.shippingIncludingTax?.value}
              currency={data?.prices?.shippingIncludingTax?.currency}
            />
          ) : (
            <Price
              data-testid="shipping"
              amount={data?.prices?.shippingExcludingTax?.value}
              currency={data?.prices?.shippingExcludingTax?.currency}
            />
          ),
      }}
      discounts={data?.prices?.appliedDiscounts?.map((discount) => {
        return {
          label: discount.label,
          price: (
            <Price
              data-testid="summary-discount-total"
              amount={-discount.amount.value}
              currency={discount.amount.currency}
              sale={true}
            />
          ),
          coupon: discount?.coupon ? (
            <span>
              <Icon source={Coupon} size="16" />
              {discount?.coupon?.code}
            </span>
          ) : undefined,
        };
      })}
      taxTotal={
        isVirtual && !data?.prices?.appliedTaxes?.length
          ? {
              price: (
                <span data-testid="tax-total-tbd">
                  {translations.taxToBeDetermined}
                </span>
              ),
            }
          : {
              price: (
                <Price
                  data-testid="tax-total-actual"
                  amount={data?.prices?.totalTax?.value}
                  currency={data?.prices?.totalTax?.currency}
                />
              ),
            }
      }
      taxesApplied={
        isVirtual
          ? undefined
          : quoteDisplaySettings?.fullSummary
          ? data?.prices?.appliedTaxes?.map((tax: any) => ({
              label: tax.label,
              price: (
                <Price
                  data-testid="applied-taxes"
                  amount={tax.amount.value}
                  currency={tax.amount.currency}
                />
              ),
            }))
          : undefined
      }
      total={{
        price:
          data?.prices?.grandTotal?.value === 0 ? (
            <span data-testid="total-including-tax">{translations.free}</span>
          ) : (
            <Price
              data-testid="total-including-tax-actual"
              amount={data?.prices?.grandTotal?.value}
              currency={data?.prices?.grandTotal?.currency}
            />
          ),
        priceWithoutTax: quoteDisplaySettings?.grandTotal ? (
          data?.prices?.grandTotalExcludingTax?.value === 0 ? (
            <span data-testid="total-excluding-tax">{translations.free}</span>
          ) : (
            <Price
              data-testid="total-excluding-tax"
              amount={data?.prices?.grandTotalExcludingTax?.value}
              currency={data?.prices?.grandTotalExcludingTax?.currency}
            />
          )
        ) : undefined,
      }}
      totalSaved={
        showTotalSaved ? (
          <Price
            amount={data?.prices?.discount?.value}
            currency={data?.prices?.grandTotal?.currency}
          />
        ) : undefined
      }
    />
  );
};
